import { MutableRefObject, useEffect, useRef, useState } from "react";

import { Box, SimpleGrid } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { useTranslation } from "react-i18next";

import { TextInput } from "components";

import BankCreditRequestEmployment from "pages/clients/view/bank-credit/request/incomes/bank-credit-request-employment";
import CreditRequestIncome from "pages/clients/view/credit/request/incomes/income";
import { BankCreditOptionsResponse, BankCreditRequestForm, BankCreditResponse } from "types";

interface BankCreditRequestIncomesProps {
  credit: BankCreditResponse;
  form: UseFormReturnType<BankCreditRequestForm>;
  options: BankCreditOptionsResponse;
  readOnly?: boolean;
  revalidateEmploymentDatesRef?: MutableRefObject<
    ((newBirthDate?: Date | undefined | null, newVisaFrom?: Date | undefined | null) => void) | null
  >;
}

const BankCreditRequestIncomes = ({
  credit,
  form,
  options,
  readOnly = false,
  revalidateEmploymentDatesRef,
}: BankCreditRequestIncomesProps) => {
  const { t } = useTranslation();

  const isInitialMount = useRef(true);
  const [partnerIncludeInApplication, setPartnerIncludeInApplication] = useState<
    boolean | undefined
  >(credit.partnerIncludeInApplication);

  const [hasAdditionalIncome, setHasAdditionalIncome] = useState<boolean>(() => {
    const incomes = credit.incomes || [];
    return incomes.some((income) => !income.partner);
  });

  const [partnerHasAdditionalIncome, setPartnerHasAdditionalIncome] = useState<boolean>(() => {
    const incomes = credit.incomes || [];
    return incomes.some((income) => income.partner);
  });

  const currentPartnerInclude = form.getValues().partnerIncludeInApplication;

  useEffect(() => {
    // Skip the effect on initial mount to avoid interfering with form initialization
    if (isInitialMount.current) {
      isInitialMount.current = false;
      setPartnerIncludeInApplication(currentPartnerInclude);
      return;
    }

    if (currentPartnerInclude !== partnerIncludeInApplication) {
      setPartnerIncludeInApplication(currentPartnerInclude);

      if (!currentPartnerInclude) {
        // Filter CURRENT form data when toggling partner OFF
        const currentValues = form.getValues();
        const currentEmployments = currentValues.employments || [];
        const currentIncomes = currentValues.incomes || [];

        const employments = currentEmployments.filter((el: any) => !el.partner);
        const incomes = currentIncomes.filter((el: any) => !el.partner);

        form.setFieldValue("employments", employments);
        form.setFieldValue("incomes", incomes);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPartnerInclude]);

  const handleAdditionalIncomeChange = (hasIncome: boolean, isPartner: boolean) => {
    if (isPartner) {
      setPartnerHasAdditionalIncome(hasIncome);
    } else {
      setHasAdditionalIncome(hasIncome);
    }

    if (!hasIncome) {
      // Clear income data for the respective partner when toggled off
      const currentValues = form.getValues();
      const currentIncomes = currentValues.incomes || [];
      const filteredIncomes = currentIncomes.filter((el: any) => el.partner !== isPartner);
      form.setFieldValue("incomes", filteredIncomes);
    }
  };

  return (
    <Box p={"md"}>
      <Box mb={40}>
        <BankCreditRequestEmployment
          credit={credit}
          form={form}
          options={options}
          partner={false}
          readOnly={readOnly}
          onAdditionalIncomeChange={handleAdditionalIncomeChange}
          hasAdditionalIncome={hasAdditionalIncome}
          onRegisterRevalidation={(fn) => {
            if (revalidateEmploymentDatesRef) {
              revalidateEmploymentDatesRef.current = fn;
            }
          }}
        />
      </Box>

      {hasAdditionalIncome && (
        <Box mt={"md"} mb={40}>
          <CreditRequestIncome
            credit={credit}
            form={form}
            options={options}
            partner={false}
            readOnly={readOnly}
            isBankCredit
          />
        </Box>
      )}
      {partnerIncludeInApplication && (
        <>
          <Box mt={"md"} mb={40}>
            <BankCreditRequestEmployment
              credit={credit}
              form={form}
              options={options}
              partner={true}
              readOnly={readOnly}
              onAdditionalIncomeChange={handleAdditionalIncomeChange}
              hasAdditionalIncome={partnerHasAdditionalIncome}
            />
          </Box>
          {partnerHasAdditionalIncome && (
            <Box mt={"md"} mb={40}>
              <CreditRequestIncome
                credit={credit}
                form={form}
                options={options}
                partner={true}
                readOnly={readOnly}
                isBankCredit
              />
            </Box>
          )}
        </>
      )}

      <SimpleGrid cols={4} mt={"lg"}>
        <TextInput
          label={t("credit.comment.singular")}
          key={form.key("comment3")}
          {...form.getInputProps("comment3")}
          disabled={readOnly}
        />
      </SimpleGrid>
    </Box>
  );
};

export { BankCreditRequestIncomes };
